import { Box, Button, Grid, TextField } from '@mui/material'
import React, { useContext, useMemo, useState } from 'react'
import "bootstrap/dist/css/bootstrap.min.css";
import "./Login.css";
import RibbonImg from "../../Images/ribbon.png"
import { Formik } from 'formik';
import * as Yup from "yup";
import { useNavigate } from 'react-router-dom';
import { CommonContext } from '../../context/CommonContext';
import { AuthContext } from '../../context/AuthContext';
import { generateCaptcha } from '../../helpers/commonHelper';
import http, { authHttp } from '../../services/commonHttpService';
import { apisPath, paths } from '../../utils/path';
import { encrypt } from '../../helpers/encryptDecrypt';
import { getDashboardPath } from '../../config/rolesConfig';
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const defaultValues = {
    username: "",
    password: "",
    captcha: "",
};

const createLoginSchema = (captchaValue) =>
    Yup.object().shape({
        username: Yup.string()
            .required("Username is required")
            .test(
                "no-only-spaces",
                "Username cannot be only spaces",
                (value) => value && value.trim().length > 0
            )
            .max(100, "Username must be less than or equal to 100 characters"),
        // .matches(
        //   REGEXS.alplabhetNumeric,
        //   "Please enter a valid Username"
        // ),

        password: Yup.string()
            .required("Password is required")
            .test(
                "no-only-spaces",
                "Password cannot be only spaces",
                (value) => value && value.trim().length > 0
            ),
        captcha: Yup.string()
            .required("Captcha is required")
            .test(
                "no-only-spaces",
                "Captcha cannot be only spaces",
                (value) => value && value.trim().length > 0
            )
            .oneOf([captchaValue], "Captcha is not correct"),
    });

const LoginWithoutHomepage = () => {
    //login
    const navigate = useNavigate();
    const { setIsLoading, setSnackState } = useContext(CommonContext);
    const { login, setUserName, setUserStateId, setUserStateName } = useContext(AuthContext);
    const [securedCaptcha, setSecuredCaptcha] = useState(generateCaptcha);
    const [error, setError] = useState("");
    const [captchaFocus, setCaptchaFocus] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleTogglePassword = () => {
        setShowPassword((prev) => !prev);
    };

    const LoginSchema = useMemo(
        () => createLoginSchema(securedCaptcha),
        [securedCaptcha]
    );

    const handleLogin = async (data, { resetForm }) => {
        setIsLoading(true);
        setError("");

        try {
            // Step 1: Login API call
            const loginResponse = await http.post(apisPath?.login, {
                username: data?.username,
                password: data?.password,
                captcha: data?.captcha,
            });

            if (loginResponse?.data?.success && loginResponse?.data?.token) {
                const token = loginResponse?.data?.token;
                const userId = loginResponse?.data?.user?.user_id;
                const userName = loginResponse?.data?.user?.fullName;
                const facilityId = loginResponse?.data?.user?.facility_id;

                // Step 2: Encrypt and store token with expiry time (30 minutes)
                const encryptedToken = encrypt(token);
                // const expiryTime = Date.now() + 10 * 60 * 1000; // 10 minutes from now
                const expiryTime = Date.now() + 30 * 60 * 1000; // 60 minutes from now
                const encryptedExpiry = encrypt(expiryTime.toString());

                // Store encrypted token and expiry time
                await localStorage.setItem("app-token", JSON.stringify(encryptedToken));
                await localStorage.setItem(
                    "token-expiry",
                    JSON.stringify(encryptedExpiry)
                );

                // Step 3: Encrypt and store userId
                const encryptedUserId = encrypt(userId?.toString() || "");
                await localStorage.setItem("user-id", JSON.stringify(encryptedUserId));

                // Step 3: Encrypt and store userName
                const encryptedUserName = encrypt(userName?.toString() || "");
                await localStorage.setItem("user-name", JSON.stringify(encryptedUserName));

                // Step 4: Encrypt and store facilityId
                const encryptedFacilityId = encrypt(facilityId?.toString() || "");
                await localStorage.setItem("facility-id", JSON.stringify(encryptedFacilityId));

                // Step 5: Fetch user profile to get role
                const profileResponse = await authHttp.get(
                    `${apisPath?.userProfile}/${userId}`
                );

                if (profileResponse?.data?.success) {
                    const roleId = profileResponse?.data?.data?.role?.role_id;
                    const stateLgd = profileResponse?.data?.data?.state_lgd;
                    const stateName = profileResponse?.data?.data?.name;
                    // const roleId = 1;
                    setUserStateId(stateLgd || null);
                    setUserStateName(stateName || "");

                    // Step 5: Encrypt and store user role ID
                    const encryptedRole = encrypt(roleId?.toString() || "");
                    await localStorage.setItem(
                        "user-role",
                        JSON.stringify(encryptedRole)
                    );

                    // Step 6: Update AuthContext with login status
                    await login(encryptedToken, encryptedRole, encryptedUserId, encryptedFacilityId, encryptedUserName);

                    // Step 7: Navigate to role-specific dashboard
                    const dashboardPath = getDashboardPath(roleId);
                    setIsLoading(false);
                    navigate(
                        `${paths?.user?.commonPoint}${paths?.user?.[dashboardPath]}`
                    );
                } else {
                    throw new Error("Failed to fetch user profile");
                }
            } else {
                throw new Error(loginResponse?.data?.message || "Login failed");
            }
        } catch (err) {
            console.error("Login Error:", err);
            setIsLoading(false);

            setSnackState((prev) => ({
                ...prev,
                open: true,
                message:
                    err?.response?.data?.message ||
                    err?.message ||
                    "Login failed. Please try again.",
                severity: "error",
            }));

            resetForm();
            setSecuredCaptcha(generateCaptcha());
        }
    };

    return (
        <Box sx={{ flexGrow: 1, height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <div className="login-card">
                        <div className="text-center mb-3">
                            <div className="d-flex justify-content-center align-items-center gap-2 mt-2">
                                <img src={RibbonImg} width="45" alt="Ribbon" />
                                <h4 className="fw-bold m-0">TCCC & DCCC Login</h4>
                            </div>
                            <p className="text-muted mt-1">
                                Sign in to access your dashboard
                            </p>
                        </div>

                        <Formik
                            initialValues={defaultValues}
                            validationSchema={LoginSchema}
                            onSubmit={handleLogin}
                        >
                            {({
                                handleChange,
                                values,
                                handleSubmit,
                                errors,
                                touched,
                                handleBlur,
                                setFieldValue,
                                isValid,
                                isSubmitting,
                                dirty,
                            }) => (
                                <>
                                    <TextField
                                        label="Username"
                                        variant="outlined"
                                        fullWidth
                                        margin="normal"
                                        size="small"
                                        name="username"
                                        value={values?.username}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(errors?.username && touched?.username)}
                                        helperText={touched?.username && errors?.username}
                                        autoComplete="off"
                                        InputLabelProps={{ style: { fontSize: 14 } }}
                                        sx={{
                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                { borderColor: "#0048ff" },
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: "#0048ff",
                                            },
                                        }}
                                    />

                                    <TextField
                                        label="Password"
                                        type={showPassword ? "text" : "password"}
                                        variant="outlined"
                                        fullWidth
                                        margin="normal"
                                        size="small"
                                        name="password"
                                        value={values?.password}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(errors?.password && touched?.password)}
                                        helperText={touched?.password && errors?.password}
                                        autoComplete="off"
                                        // onPaste={(e) => e.preventDefault()}
                                        // onDrop={(e) => e.preventDefault()}
                                        InputLabelProps={{ style: { fontSize: 14 } }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={handleTogglePassword}
                                                        edge="end"
                                                        tabIndex={-1}
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                { borderColor: "#0048ff" },
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: "#0048ff",
                                            },
                                        }}
                                    />

                                    {/* CAPTCHA BOX */}
                                    <div
                                        className={`captcha-wrapper ${captchaFocus ? "captcha-active" : ""
                                            }`}
                                    >
                                        <div className="captcha-img-react">
                                            {securedCaptcha}
                                        </div>

                                        <input
                                            type="text"
                                            className="captcha-input"
                                            placeholder="Enter captcha"
                                            name="captcha"
                                            value={values?.captcha}
                                            onChange={handleChange}
                                            onBlur={(e) => {
                                                handleBlur(e);
                                                setCaptchaFocus(false);
                                            }}
                                            onFocus={() => setCaptchaFocus(true)}
                                            autoComplete="off"
                                            onPaste={(e) => e.preventDefault()}
                                            onDrop={(e) => e.preventDefault()}
                                            maxLength={6}
                                        />
                                    </div>

                                    {errors?.captcha && touched?.captcha && (
                                        <p className="captcha-error">{errors?.captcha}</p>
                                    )}

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        className="login-btn"
                                        onClick={handleSubmit}
                                        disabled={!(isValid && dirty) || isSubmitting}
                                    >
                                        Sign In to Dashboard
                                    </Button>
                                </>
                            )}
                        </Formik>

                        <p className="footer-text mt-4 text-center">
                            © All right reserved by Ministry of Health & Family Welfare,
                            <br />
                            Government Of India.
                        </p>
                    </div>
                </Grid>
            </Grid>
        </Box>
    )
}

export default LoginWithoutHomepage